
import { GameMode } from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import { GameModel } from "@nolimitcity/slot-game/bin/model/GameModel";
import { ApiPlugin } from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import { BaseServerData, CarFsInfo, IRestoreData } from './IData';
import { ISymbolValues } from "../../../dev/extra/MiniPayTable";
import { WinGameData, ReelGameData } from "./GameDataModels";
import { Helper } from '../../../dev/utils/Helper';
import { ArrayHelper } from '../../../dev/utils/ArrayHelper';
import { APIEvent } from '@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem';
import { APIFeatureBetEventData } from '@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData';

export class GameData {

    public raw: BaseServerData;

    public Win: WinGameData;
    public Reel: ReelGameData;

    public get isRestoreState(): boolean {
        return this.raw.isRestoreState;
    }

    public get mode(): string {
        return this.raw.mode;
    }

    public get fsRestoreData(): IRestoreData {
        return Helper.fetchOrApply(this.raw.restoreData,{});
    }

    public get isNormalMode(): boolean {
        return this.raw.mode === GameMode.NORMAL;
    }

    public get isFreeSpin(): boolean {
        return this.raw.mode === GameMode.FREESPIN;
    }

    public get nextIsRespin():boolean{
        return this.raw.nextMode === "RESPIN";
    }

    public get nextMode(): string {
        return this.raw.nextMode;
    }

    public get nextModeSetup(): boolean {
        return this.raw.nextMode !== GameMode.NORMAL && this.raw.nextMode === "FREESPIN_SETUP" && this.raw.mode !== "FREESPIN_SETUP";
    }

    public get lastFSRound(): boolean {
        return this.raw.mode === GameMode.FREESPIN && this.raw.nextMode === GameMode.NORMAL;
    }

    public get isNextFS(): boolean {
        return this.raw.mode !== GameMode.FREESPIN && this.raw.nextMode === GameMode.FREESPIN;
    }

    public get isLastSetupFS(): boolean {
        return this.raw.mode === "FREESPIN_SETUP" && this.raw.nextMode !== "FREESPIN_SETUP";
    }

    public get gameModeChanges(): boolean {
        return this.raw.mode !== this.raw.nextMode;
    }

    public get carsActiveInFS(): string[] {
        return Object.keys(Helper.fetchOrApply(this.fsInfo.fsCars,{}));
    }

    public get allFsCarValue(): number[][] {
        const keys:string[] = this.carsActiveInFS;
        return keys.length ? ArrayHelper.initArrayWithValues(keys.length, (i) => this.fsInfo.fsCars[keys[i]]) : [];
    }

    public get addedExtraFsSpins(): number {
        return Helper.fetchOrApply(this.fsInfo.addedNumberOfFreespinsThisSpin,0);
    }

    public get fsInfo(): CarFsInfo {
        return Helper.fetchOrApply(this.raw.fsInfo,{});
    }
     
    constructor(){
        this.Reel = new ReelGameData();
        this.Win = new WinGameData();
    }

    public setData(data:any):void{
        this.raw = data;
        this.Reel.apply(data)
        this.Win.apply(data)
    }
}

export class InitData extends GameData {

    public get isRestoreState(): boolean {
        return this.raw.isRestoreState;
    }

    public get symbolValues(): ISymbolValues {
        return this.raw.symbolValues;
    }

    setData(data: any) {
        super.setData(data);
    }
}

export class BaseModel extends GameModel<InitData, GameData> {

    public static buyFeatureName : string = "";
    private static api : ApiPlugin;

    private trigg:boolean = true;
    
    constructor(_api: ApiPlugin) {
        super(_api);

        BaseModel.api = _api;
        
        this.gameData = new GameData();
        this.initData = new InitData();

        this.applyBelowStakeCheck(_api);
    }
    
    private applyBelowStakeCheck(api: ApiPlugin):void{
        if(this.trigg){
            api.events.once("featureName", (data: string) => BaseModel.buyFeatureName = data);
            this.trigg = false;
        }

        
        api.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => {this.onBet(betData);});
        api.events.on(APIEvent.ACTION_SPINS_BET, (betData: APIFeatureBetEventData) => {this.onBet(betData);});

        this.gameData.Win.isWinBelowStake = () => this.isWinBelowStake;
        this.initData.Win.isWinBelowStake = () => this.isWinBelowStake;

        this.gameData.Win.isIndiviWinBelowStake = () => this.isIndiviWinBelowStake;
        this.initData.Win.isIndiviWinBelowStake = () => this.isIndiviWinBelowStake;
    }

    private onBet(betData: APIFeatureBetEventData): void {
        const activeFeature = BaseModel.api.betFeatureController.getActiveBetFeature();
        if (activeFeature){
            BaseModel.buyFeatureName = activeFeature.name;
        } else {
            BaseModel.buyFeatureName = ""
        }
    }

    private get getAccumulatedRoundWin(): number {
        return (this.gameData?.Win.totalWin || this.initData.Win.totalWin);
    }

    public get isWinBelowStake(): boolean {
        return BaseModel.api.gameClientConfiguration.belowStakeWinRestriction && (this.getAccumulatedRoundWin <= this.getBuyFeaturePrice);
    }

    public get isIndiviWinBelowStake(): boolean {
        return BaseModel.api.gameClientConfiguration.belowStakeWinRestriction && this.gameData.Win.winRatio <= 1;
    }

    public get getBuyFeaturePrice(): number {
        const betLevel : string = BaseModel.api.freeBets.hasFreeBets() ? BaseModel.api.freeBets.getBet() : BaseModel.api.betLevel.getLevel();
        let price: number = this.gameData?.Win.playedBetValue || parseFloat(betLevel);
        const activeFeature  = BaseModel.api.betFeatureController.getActiveBetFeature();
        if (activeFeature) {
            price = activeFeature.getTotalCost();
        }
        // else if(BaseModel.api.gameClientConfiguration.belowStakeWinRestriction && (this.gameData.raw.wasFeatureBuy)){
        //     let max:number = 1;
        //     this.initData.featureBuyTimesBetValue.forEach(v => { max = Math.max(max,v.price)})
        //     price *= max;
        // }
        return price;
    }

    protected onInit(data: any): void {this.data = data; this.initData.setData(data); this.gameData.setData(data); }

    protected onGame(data: any): void {this.data = data; this.gameData.setData(data); }
}